import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { MainLayout } from '../../components/layout';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Badge from '../../components/ui/Badge';
import Loading from '../../components/ui/Loading';
import Alert from '../../components/ui/Alert';
import { ArrowLeft, Building2, Mail, Phone, MapPin, Calendar, Edit2, Briefcase } from 'lucide-react';
import { tenantService } from '../../services';
import type { Tenant, DropdownItem, CountryItem, StateItem, CityItem } from '../../types';

const ViewTenantPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchTenant = async () => {
      if (!id) return;

      setIsLoading(true);
      try {
        const data = await tenantService.getTenantById(Number(id));
        
        // Fetch lookup data to get names from IDs
        const rawData = data as any;
        const businessTypeId = rawData.business_type || data.business_type_id;
        const countryId = data.country_id || rawData.country;
        const stateId = data.state_id || rawData.state;
        const cityId = data.city_id || rawData.city;
        
        // Fetch lookup data in parallel
        const [businessTypesRes, countriesRes] = await Promise.all([
          tenantService.getBusinessTypes(),
          tenantService.getCountries(),
        ]);
        
        // Find business type name
        const businessType = businessTypesRes.items?.find((bt: DropdownItem) => bt.key === businessTypeId);
        
        // Find country name
        const country = countriesRes.items?.find((c: CountryItem) => c.countryid === countryId);
        
        // Fetch states and cities if we have IDs
        let stateName = '';
        let cityName = '';
        
        if (countryId) {
          const statesRes = await tenantService.getStates(countryId);
          const state = statesRes.items?.find((s: StateItem) => s.stateid === stateId);
          stateName = state?.statename || '';
          
          if (stateId) {
            const citiesRes = await tenantService.getCities(stateId);
            const city = citiesRes.items?.find((c: CityItem) => c.cityid === cityId);
            cityName = city?.cityname || '';
          }
        }
        
        // Transform data to expected format
        const transformedTenant: Tenant = {
          ...data,
          address_line_1: rawData.address1 || data.address_line_1 || '',
          address_line_2: rawData.address2 || data.address_line_2 || '',
          postal_code: data.postal_code || rawData.postalcode || '',
          business_type_id: businessTypeId,
          business_type_name: businessType?.value || '',
          country_id: countryId,
          country_name: country?.countryname || '',
          state_id: stateId,
          state_name: stateName,
          city_id: cityId,
          city_name: cityName,
          status: data.status === true || rawData.status === 'Y',
        };
        
        setTenant(transformedTenant);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load tenant');
      } finally {
        setIsLoading(false);
      }
    };
    fetchTenant();
  }, [id]);

  if (isLoading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center min-h-96">
          <Loading size="lg" />
        </div>
      </MainLayout>
    );
  }

  if (!tenant) {
    return (
      <MainLayout>
        <div className="flex flex-col items-center justify-center min-h-96">
          <h2 className="text-xl font-semibold text-text-primary mb-2">Tenant Not Found</h2>
          <p className="text-text-muted mb-4">The tenant you're looking for doesn't exist.</p>
          <Button onClick={() => navigate('/tenants')}>Back to Tenants</Button>
        </div>
      </MainLayout>
    );
  }

  // Build address string
  const addressParts = [
    tenant.address_line_1,
    tenant.address_line_2,
    tenant.city_name,
    tenant.state_name,
    tenant.country_name,
    tenant.postal_code,
  ].filter(Boolean);
  const fullAddress = addressParts.join(', ') || 'No address provided';

  return (
    <MainLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/tenants')}>
              <ArrowLeft size={18} />
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-text-primary">{tenant.company_name}</h1>
              <p className="text-text-muted mt-1">{tenant.company_code || 'No code'}</p>
            </div>
          </div>
          <Button onClick={() => navigate(`/tenants/${id}/edit`)}>
            <Edit2 size={18} className="mr-2" />
            Edit
          </Button>
        </div>

        {error && (
          <Alert variant="error" message={error} onClose={() => setError(null)} />
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Info Card */}
          <Card className="lg:col-span-2">
            <div className="p-6">
              <div className="flex items-start gap-4 mb-6">
                <div className="w-16 h-16 bg-accent-primary/10 rounded-xl flex items-center justify-center">
                  <Building2 size={32} className="text-accent-primary" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h2 className="text-xl font-semibold text-text-primary">{tenant.company_name}</h2>
                    <Badge variant={tenant.status ? 'success' : 'danger'}>
                      {tenant.status ? 'Active' : 'Inactive'}
                    </Badge>
                  </div>
                  <p className="text-text-muted">{tenant.company_code || 'No company code'}</p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Contact Info */}
                <div className="space-y-4">
                  <h3 className="text-sm font-medium text-text-muted uppercase tracking-wide">Contact Information</h3>
                  
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-surface-secondary rounded-lg flex items-center justify-center">
                      <Mail size={16} className="text-text-muted" />
                    </div>
                    <div>
                      <p className="text-xs text-text-muted">Email</p>
                      <p className="text-text-primary">{tenant.email || 'N/A'}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-surface-secondary rounded-lg flex items-center justify-center">
                      <Phone size={16} className="text-text-muted" />
                    </div>
                    <div>
                      <p className="text-xs text-text-muted">Phone</p>
                      <p className="text-text-primary">{tenant.phone || 'N/A'}</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 bg-surface-secondary rounded-lg flex items-center justify-center">
                      <MapPin size={16} className="text-text-muted" />
                    </div>
                    <div>
                      <p className="text-xs text-text-muted">Address</p>
                      <p className="text-text-primary">{fullAddress}</p>
                    </div>
                  </div>
                </div>

                {/* Business Info */}
                <div className="space-y-4">
                  <h3 className="text-sm font-medium text-text-muted uppercase tracking-wide">Business Information</h3>
                  
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-surface-secondary rounded-lg flex items-center justify-center">
                      <Briefcase size={16} className="text-text-muted" />
                    </div>
                    <div>
                      <p className="text-xs text-text-muted">Business Type</p>
                      <p className="text-text-primary">{tenant.business_type_name || 'N/A'}</p>
                    </div>
                  </div>

                  {tenant.sub_business_name && (
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-surface-secondary rounded-lg flex items-center justify-center">
                        <Briefcase size={16} className="text-text-muted" />
                      </div>
                      <div>
                        <p className="text-xs text-text-muted">Sub Business</p>
                        <p className="text-text-primary">{tenant.sub_business_name}</p>
                      </div>
                    </div>
                  )}

                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-surface-secondary rounded-lg flex items-center justify-center">
                      <Building2 size={16} className="text-text-muted" />
                    </div>
                    <div>
                      <p className="text-xs text-text-muted">Service Provider</p>
                      <p className="text-text-primary">{tenant.parent_company_name || 'None (Top-level)'}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-surface-secondary rounded-lg flex items-center justify-center">
                      <span className="text-xs font-semibold text-text-muted">LV</span>
                    </div>
                    <div>
                      <p className="text-xs text-text-muted">Hierarchy Level</p>
                      <p className="text-text-primary">Level {tenant.level}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Card>

          {/* Side Info Card */}
          <Card>
            <div className="p-6">
              <h3 className="text-sm font-medium text-text-muted uppercase tracking-wide mb-4">Details</h3>
              
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <Calendar size={16} className="text-text-muted" />
                  <div>
                    <p className="text-xs text-text-muted">Created</p>
                    <p className="text-sm text-text-primary">
                      {tenant.created_date 
                        ? new Date(tenant.created_date).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric',
                          })
                        : 'N/A'}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <Calendar size={16} className="text-text-muted" />
                  <div>
                    <p className="text-xs text-text-muted">Last Modified</p>
                    <p className="text-sm text-text-primary">
                      {tenant.modified_date 
                        ? new Date(tenant.modified_date).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric',
                          })
                        : 'N/A'}
                    </p>
                  </div>
                </div>

                {tenant.valid_until && (
                  <div className="flex items-center gap-3">
                    <Calendar size={16} className="text-text-muted" />
                    <div>
                      <p className="text-xs text-text-muted">Valid Until</p>
                      <p className="text-sm text-text-primary">
                        {new Date(tenant.valid_until).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                        })}
                      </p>
                    </div>
                  </div>
                )}
              </div>

              <div className="mt-6 pt-6 border-t border-surface-border">
                <Button 
                  variant="secondary" 
                  fullWidth
                  onClick={() => navigate(`/tenants/${id}/edit`)}
                >
                  <Edit2 size={16} className="mr-2" />
                  Edit Tenant
                </Button>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </MainLayout>
  );
};

export default ViewTenantPage;
